import { compact, uniqBy } from "lodash";
import { AssessmentQuestionType } from "types/graphql-schema";

import {
  AssessmentQuestion,
  AssessmentQuestionContainer,
  AssessmentQuestionRole,
} from "./types";

export const getQuestionRole = (question: AssessmentQuestion) =>
  "role" in question
    ? question.role
    : "responsibility" in question
      ? question.responsibility?.role
      : undefined;

export const hasAnsweredNextRoleQuestions = (
  questions: AssessmentQuestionContainer[],
  currentRoleIds: readonly (number | null)[],
  answers: readonly ({
    questionId: number;
    integerAnswer?: number | null;
  } | null)[],
) =>
  questions.some(({ question }) => {
    const role = getQuestionRole(question);
    return (
      role &&
      !currentRoleIds.includes(role.id) &&
      answers.some(
        (answer) =>
          answer?.questionId === question.id &&
          Number.isInteger(answer.integerAnswer),
      )
    );
  });

export const isRoleBasedQuestionType = (
  questionType: AssessmentQuestionType,
) => {
  return [
    AssessmentQuestionType.Competency,
    AssessmentQuestionType.CompetencyCriteria,
    AssessmentQuestionType.Responsibility,
  ].includes(questionType);
};

export const isGoalBasedQuestionType = (
  questionType: AssessmentQuestionType,
) => {
  return [
    AssessmentQuestionType.OverallGoal,
    AssessmentQuestionType.IndividualGoal,
  ].includes(questionType);
};

export const isCoreValueBasedQuestionType = (
  questionType: AssessmentQuestionType,
) => {
  return [
    AssessmentQuestionType.IndividualRecognitionCoreValue,
    AssessmentQuestionType.OverallRecognitionCoreValue,
  ].includes(questionType);
};

export const getQuestionsByRole = (
  questions: AssessmentQuestionContainer[],
  roleFilter: (role: AssessmentQuestionRole) => boolean = () => true,
) => {
  return questions.reduce(
    (acc, questionWithWeight) => {
      const { question } = questionWithWeight;
      const role = getQuestionRole(question);
      if (!role) {
        throw new Error("Expected roll to not be null");
      }
      if (!roleFilter(role)) {
        return acc;
      }
      const roleId = role.id;
      const existing = acc.roleBasedQuestionsByRoleId[roleId] ?? [];
      return {
        roleBasedQuestionsByRoleId: {
          ...acc.roleBasedQuestionsByRoleId,
          [roleId]: [...existing, questionWithWeight],
        },
        roles: uniqBy(compact([...acc.roles, role]), "id"),
      };
    },
    { roles: [], roleBasedQuestionsByRoleId: {} } as {
      roles: AssessmentQuestionRole[];
      roleBasedQuestionsByRoleId: {
        [role: number]: AssessmentQuestionContainer[];
      };
    },
  );
};
